import { apiGet, apiPostPut, apiDelete } from "./api_methods";
import { RECRUITER_ASSESSMENTS_BASE } from "./endpoints";

const INTERVIEWS = `${RECRUITER_ASSESSMENTS_BASE}/interviews`;
const MOCK_TESTS = `${RECRUITER_ASSESSMENTS_BASE}/mock-tests`;
const CODING_INTERVIEWS = `${RECRUITER_ASSESSMENTS_BASE}/coding-interviews`;

/**
 * Get interview invitation with applicants (GET /interviews/:id/applicants).
 * @param {string} id - Interview invitation ID
 * @param {Object} params - { skip?, limit?, clientId? }
 * @returns {Promise<{ body, status }>}
 */
export async function getInterviewApplicants(id, params = {}) {
  const q = {};
  if (params.skip != null) q.skip = params.skip;
  if (params.limit != null) q.limit = params.limit;
  if (params.clientId != null) q.clientId = params.clientId;
  const query = new URLSearchParams(q).toString();
  const path = query
    ? `${INTERVIEWS}/${id}/applicants?${query}`
    : `${INTERVIEWS}/${id}/applicants`;
  return apiGet(path);
}

/**
 * Update interview invitation (PUT /interviews/:id).
 * @param {string} id - Interview invitation ID
 * @param {Object} body - Partial invitation fields (e.g. isOpen, technology, levelOfInterview)
 * @returns {Promise<{ body, status }>}
 */
export async function updateInterview(id, body) {
  return apiPostPut(body, `${INTERVIEWS}/${id}`, "PUT");
}

/**
 * Delete (soft-delete) interview invitation (DELETE /interviews/:id).
 * @param {string} id - Interview invitation ID
 * @returns {Promise<{ body, status }>}
 */
export async function deleteInterview(id) {
  return apiDelete(`${INTERVIEWS}/${id}`);
}

/**
 * Get single interview invitation details (GET /interviews/:id).
 * @param {string} id - Interview invitation ID
 * @returns {Promise<{ body, status }>}
 */
export async function getInterview(id) {
  return apiGet(`${INTERVIEWS}/${id}`);
}

/**
 * Create standalone interview invitation (POST /interviews).
 * @param {Object} body - Invitation payload (technology, levelOfInterview, interviewType, noOfQuestions, etc.)
 * @returns {Promise<{ body, status }>} 201; body.data may include interviewLink
 */
export async function createInterview(body) {
  return apiPostPut(body, INTERVIEWS, "POST");
}

/**
 * Get mock test invitation with applicants (GET /mock-tests/:id/applicants).
 * @param {string} id - Mock test invitation ID
 * @param {Object} params - { skip?, limit?, clientId? }
 * @returns {Promise<{ body, status }>}
 */
export async function getMockTestApplicants(id, params = {}) {
  const q = {};
  if (params.skip != null) q.skip = params.skip;
  if (params.limit != null) q.limit = params.limit;
  if (params.clientId != null) q.clientId = params.clientId;
  const query = new URLSearchParams(q).toString();
  const path = query
    ? `${MOCK_TESTS}/${id}/applicants?${query}`
    : `${MOCK_TESTS}/${id}/applicants`;
  return apiGet(path);
}

/**
 * Update mock test invitation (PUT /mock-tests/:id).
 * @param {string} id - Mock test ID
 * @param {Object} body - Partial mock test fields (e.g. isOpen, mockTestName, cutOff)
 * @returns {Promise<{ body, status }>}
 */
export async function updateMockTest(id, body) {
  return apiPostPut(body, `${MOCK_TESTS}/${id}`, "PUT");
}

/**
 * Delete (soft-delete) mock test (DELETE /mock-tests/:id).
 * @param {string} id - Mock test ID
 * @returns {Promise<{ body, status }>}
 */
export async function deleteMockTest(id) {
  return apiDelete(`${MOCK_TESTS}/${id}`);
}

/**
 * Create standalone mock test invitation (POST /mock-tests).
 * @param {Object} body - mockTestName, noOfTechnicalQuestions, noOfAptitudeQuestions, etc.
 * @returns {Promise<{ body, status }>} 201
 */
export async function createMockTest(body) {
  return apiPostPut(body, MOCK_TESTS, "POST");
}

/**
 * Get coding interview invitation with applicants (GET /coding-interviews/:id/applicants).
 * @param {string} id - Coding interview invitation ID
 * @param {Object} params - { skip?, limit? }
 * @returns {Promise<{ body, status }>}
 */
export async function getCodingInterviewApplicants(id, params = {}) {
  const q = {};
  if (params.skip != null) q.skip = params.skip;
  if (params.limit != null) q.limit = params.limit;
  const query = new URLSearchParams(q).toString();
  const path = query
    ? `${CODING_INTERVIEWS}/${id}/applicants?${query}`
    : `${CODING_INTERVIEWS}/${id}/applicants`;
  return apiGet(path);
}
